import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { MupLayout } from "@/pages/mup/MupLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel,
} from "@/components/ui/select";
import { Search, ChevronLeft, ChevronRight, AlertTriangle, CheckCircle2 } from "lucide-react";
import MupValutazioneProdotto, { RICEVUTA_DECLS } from "@/pages/mup/MupValutazioneProdotto";

const STEPS = ["Cliente", "Intermediario", "Compagnia e prodotto", "Valutazione del prodotto", "Note e consensi", "Riepilogo e invio"];
const TRI = [
  { v: "non_compilato", l: "—" },
  { v: "si", l: "Sì" },
  { v: "no", l: "No" },
  { v: "na", l: "N/A" },
];
const tipoDaNome = (n) => {
  const t = (n || "").toLowerCase();
  if (/coop/.test(t)) return "cooperativa";
  if (/fondazione/.test(t)) return "fondazione";
  if (/associazione|\bass\.|\baps\b|\bodv\b|\basd\b/.test(t)) return "associazione";
  if (/\bets\b|onlus/.test(t)) return "ets";
  if (/societa|società|\bs\.?r\.?l|\bs\.?p\.?a|\bs\.?n\.?c|\bs\.?a\.?s/.test(t)) return "societa";
  return "persona_fisica";
};
const CLIENT_TYPES = ["persona_fisica", "azienda", "cooperativa", "ets", "associazione", "fondazione", "societa"];
const CONSENTS = [
  { key: "trattamento", label: "Consenso al trattamento dei dati personali e alla comunicazione a terzi per le finalità dichiarate", def: "autorizzo" },
  { key: "dati_particolari", label: "Trattamento di eventuali dati particolari (es. dati relativi alla salute) necessari per le operazioni richieste", def: "non_autorizzo" },
  { key: "mkt_propri", label: "4a) Promozione/offerta di prodotti e servizi propri della Società e ricerche di mercato", def: "non_autorizzo" },
  { key: "mkt_terzi", label: "4b) Promozione/offerta di prodotti e servizi di terzi", def: "non_autorizzo" },
  { key: "com_terzi_propri", label: "4c) Comunicazione dei dati a terzi per promozione/offerta di servizi della Società", def: "non_autorizzo" },
  { key: "com_terzi_terzi", label: "4d) Comunicazione dei dati a terzi per promozione/offerta di prodotti/servizi dei terzi medesimi", def: "non_autorizzo" },
];
const DEFAULT_CONSENTS = CONSENTS.reduce((a, c) => ({ ...a, [c.key]: c.def }), {});
const DELIVERY = [
  { v: "email", l: "Email" },
  { v: "cartaceo", l: "Cartaceo" },
  { v: "supporto_durevole", l: "Supporto durevole elettronico" },
];

const emptyClient = {
  client_type: "persona_fisica", name: "", fiscal_code: "", vat: "", legal_seat: "",
  address: "", cap: "", city: "", province: "", pec: "", email: "", mobile: "", phone: "",
  legal_rep: "", legal_rep_fiscal_code: "", signer_role: "", delivery_pref: "email",
  e_delivery_consent: false, notes: "",
};

export default function MupWizard() {
  const navigate = useNavigate();
  const { id } = useParams();
  const editing = Boolean(id);
  const [step, setStep] = useState(0);
  const [products, setProducts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [intermediaries, setIntermediaries] = useState([]);
  const [areas, setAreas] = useState([]);
  const [clientResults, setClientResults] = useState([]);
  const [clientQuery, setClientQuery] = useState("");
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    client_data: { ...emptyClient },
    intermediary_id: "", company_id: "", product_id: "", proposal_number: "",
    compilation_date: new Date().toISOString().slice(0, 10),
    distribution_channel: "Agenzia", grid: [], incoherence_motivation: "", product_motivation: "",
    note_client: "", note_intermediary: "", note_internal: "", consents: { ...DEFAULT_CONSENTS }, otp_signature: true,
    banca_etica_segnalazione: false, rating_sostenibilita_eticapro: "",
    area_id: "", area_titolo: "", needs_grid: [], prodotto_rows: [],
    consulenza: {}, dichiarazione_coerenza: false, ricevuta_contraente: {}, salva_assieasy: false,
  });

  const loadCatalog = () => {
    api.get("/mup/products").then(({ data }) => setProducts(data));
    api.get("/mup/companies").then(({ data }) => setCompanies(data));
  };

  useEffect(() => {
    api.get("/mup/products").then(({ data }) => setProducts(data));
    api.get("/mup/companies").then(({ data }) => setCompanies(data));
    api.get("/mup/intermediaries").then(({ data }) => setIntermediaries(data));
    api.get("/mup/aree-tutela").then(({ data }) => setAreas(data));
    if (editing) {
      api.get(`/mup/pratiche/${id}`).then(({ data }) => {
        setForm((f) => ({ ...f, ...data, client_data: { ...emptyClient, ...(data.client_data || {}) }, consents: { ...DEFAULT_CONSENTS, ...(data.consents || {}) } }));
      });
    }
  }, [id, editing]);

  const setCD = (k, v) => setForm((f) => ({ ...f, client_data: { ...f.client_data, [k]: v } }));
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const searchClients = () => {
    api.get("/mup/clients", { params: { query: clientQuery } }).then(({ data }) => setClientResults(data));
  };
  const pickClient = (c) => {
    setForm((f) => ({ ...f, client_id: c.client_id, client_data: { ...emptyClient, ...c } }));
    setClientResults([]);
    toast.success(`Cliente ${c.name} caricato`);
  };

  const [sp] = useSearchParams();
  useEffect(() => {
    if (editing) return;
    const ente = sp.get("ente"); const nome = sp.get("nome");
    if (!nome && !ente) return;
    api.get("/mup/clients", { params: { query: nome || "" } }).then(({ data }) => {
      const match = data.find((c) => c.client_id === ente) || data.find((c) => (c.name || "").toLowerCase() === (nome || "").toLowerCase());
      if (match) pickClient({ ...match, client_type: match.client_type && match.client_type !== "persona_fisica" ? match.client_type : tipoDaNome(match.name) });
      else if (nome) setForm((f) => ({ ...f, client_data: { ...f.client_data, name: nome, client_type: tipoDaNome(nome) } }));
    }).catch(() => { if (nome) setForm((f) => ({ ...f, client_data: { ...f.client_data, name: nome, client_type: tipoDaNome(nome) } })); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp, editing]);

  const [siAvviso, setSiAvviso] = useState(null);
  const siDone = useRef(null);
  useEffect(() => {
    const si = sp.get("smart_import");
    if (editing || !si || siDone.current === si) return;
    siDone.current = si;
    api.get(`/ai/smartimport/${si}/prefill-mup`).then(({ data }) => {
      setForm((f) => (f.note_internal.includes(data.note_internal) ? f
        : { ...f, note_internal: f.note_internal ? `${f.note_internal}\n\n${data.note_internal}` : data.note_internal }));
      setSiAvviso(data);
    }).catch((e) => toast.error(e.response?.data?.detail || "Dati Smart Import non disponibili"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp, editing]);

  const selectProduct = (pid) => {
    const prod = products.find((p) => p.product_id === pid);
    const grid = (prod?.grid_rows || []).map((voce) => ({
      voce, richieste: "non_compilato", proposte: "non_compilato", confermate: "non_compilato", nota: "",
    }));
    setForm((f) => ({ ...f, product_id: pid, company_id: prod?.company_id || f.company_id, grid: editing && f.product_id === pid ? f.grid : grid }));
  };

  useEffect(() => {
    if (editing) return;
    const prodName = sp.get("prodotto");
    if (!prodName || !products.length || form.product_id) return;
    const p = products.find((x) => (x.name || "").toLowerCase() === prodName.toLowerCase())
      || products.find((x) => (x.name || "").toLowerCase().includes(prodName.toLowerCase()));
    if (p) selectProduct(p.product_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp, products, editing]);

  const setGrid = (i, k, v) => setForm((f) => {
    const grid = [...f.grid];
    grid[i] = { ...grid[i], [k]: v };
    return { ...f, grid };
  });

  const incoherences = useMemo(() =>
    form.grid.filter((r) => r.richieste !== "non_compilato" && r.proposte !== "non_compilato" && r.richieste !== r.proposte),
  [form.grid]);

  const activeIntermediaries = intermediaries.filter((i) => i.active);

  const save = async (redirect = true) => {
    setSaving(true);
    try {
      let praticaId = id;
      if (editing) {
        await api.put(`/mup/pratiche/${id}`, form);
      } else {
        const { data } = await api.post("/mup/pratiche", form);
        praticaId = data.pratica_id;
        if (sp.get("trattativa")) {
          await api.post(`/trattative/${sp.get("trattativa")}/mup`, { pratica_id: praticaId }).catch(() => toast.error("MUP salvato ma non collegato alla trattativa"));
        }
        // salva anche il cliente in anagrafica se nuovo
        if (!form.client_id && form.client_data.name) {
          api.post("/mup/clients", form.client_data).catch(() => {});
        }
      }
      toast.success("Pratica salvata");
      if (redirect) navigate(`/mup/pratiche/${praticaId}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const prod = products.find((p) => p.product_id === form.product_id);
  const comp = companies.find((c) => c.company_id === form.company_id);
  const inter = intermediaries.find((i) => i.intermediary_id === form.intermediary_id);

  return (
    <MupLayout subtitle={editing ? "Modifica pratica" : "Nuova pratica"} title="Compilazione MUP">
      {siAvviso && (
        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="mup-smart-import-banner">
          <b>Bozza da Assietica Smart Import</b> ({siAvviso.nome_file}): {siAvviso.avviso} I dati della polizza concorrente sono nelle <b>Note interne</b>.
          <button onClick={() => setSiAvviso(null)} className="ml-3 underline text-amber-700" data-testid="mup-smart-import-banner-close">Chiudi</button>
        </div>
      )}
      {/* Stepper */}
      <div className="flex items-center gap-1 mb-8 overflow-x-auto pb-2">
        {STEPS.map((s, i) => (
          <button
            key={s}
            data-testid={`mup-step-${i}`}
            onClick={() => setStep(i)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm whitespace-nowrap transition-all ${
              i === step ? "bg-brand-blue text-white" : i < step ? "text-brand-blue" : "text-slate-400"
            }`}
          >
            <span className={`h-5 w-5 rounded-full flex items-center justify-center text-xs border ${i === step ? "border-white" : "border-current"}`}>{i + 1}</span>
            <span className="hidden md:inline">{s}</span>
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-6 min-h-[360px]">
        {step === 0 && (
          <div className="space-y-5">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input data-testid="mup-client-search" value={clientQuery} onChange={(e) => setClientQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchClients()} placeholder="Cerca cliente esistente per CF, P.IVA o nome…" className="pl-9 bg-white border-slate-300" />
              </div>
              <Button variant="outline" onClick={searchClients}>Cerca</Button>
            </div>
            {clientResults.length > 0 && (
              <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
                {clientResults.map((c) => (
                  <button key={c.client_id} onClick={() => pickClient(c)} className="w-full text-left px-4 py-2 hover:bg-slate-50 text-sm">
                    <span className="font-medium text-slate-900">{c.name}</span> <span className="text-slate-500">· {c.fiscal_code || c.vat}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Tipo cliente">
                <Select value={form.client_data.client_type} onValueChange={(v) => setCD("client_type", v)}>
                  <SelectTrigger data-testid="mup-client-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{CLIENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace("_", " ")}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Nome e cognome / Ragione sociale *"><Input data-testid="mup-client-name" value={form.client_data.name} onChange={(e) => setCD("name", e.target.value)} /></Field>
              <Field label="Codice Fiscale"><Input data-testid="mup-client-cf" value={form.client_data.fiscal_code} onChange={(e) => setCD("fiscal_code", e.target.value)} /></Field>
              <Field label="Partita IVA"><Input data-testid="mup-client-vat" value={form.client_data.vat} onChange={(e) => setCD("vat", e.target.value)} /></Field>
              <Field label="Indirizzo"><Input value={form.client_data.address} onChange={(e) => setCD("address", e.target.value)} /></Field>
              <div className="grid grid-cols-3 gap-2">
                <Field label="CAP"><Input value={form.client_data.cap} onChange={(e) => setCD("cap", e.target.value)} /></Field>
                <Field label="Comune"><Input value={form.client_data.city} onChange={(e) => setCD("city", e.target.value)} /></Field>
                <Field label="Prov."><Input value={form.client_data.province} onChange={(e) => setCD("province", e.target.value)} /></Field>
              </div>
              <Field label="Email"><Input data-testid="mup-client-email" value={form.client_data.email} onChange={(e) => setCD("email", e.target.value)} /></Field>
              <Field label="Cellulare"><Input data-testid="mup-client-mobile" value={form.client_data.mobile} onChange={(e) => setCD("mobile", e.target.value)} /></Field>
              <Field label="PEC"><Input value={form.client_data.pec} onChange={(e) => setCD("pec", e.target.value)} /></Field>
              <Field label="Legale rappresentante"><Input value={form.client_data.legal_rep} onChange={(e) => setCD("legal_rep", e.target.value)} /></Field>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4 max-w-md">
            <Field label="Intermediario proponente *">
              <Select value={form.intermediary_id} onValueChange={(v) => set("intermediary_id", v)}>
                <SelectTrigger data-testid="mup-intermediary-select"><SelectValue placeholder="Seleziona intermediario attivo" /></SelectTrigger>
                <SelectContent>{activeIntermediaries.map((i) => <SelectItem key={i.intermediary_id} value={i.intermediary_id}>{i.name} {i.rui_number ? `· RUI ${i.rui_number}` : ""}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            {activeIntermediaries.length === 0 && <p className="text-sm text-amber-600">Nessun intermediario attivo. Aggiungine uno dal Catalogo.</p>}
          </div>
        )}

        {step === 2 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
            <div className="md:col-span-2 rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm text-slate-700" data-testid="mup-multi-company-hint">
              Qui indichi la compagnia e il prodotto <b>principale/raccomandato</b>: da questi si genera la tabella di coerenza (obbligo IVASS).
              Per <b>confrontare più compagnie</b> (es. Groupama e Assimoco) in un <b>unico MUP</b>, aggiungi gli altri prodotti nella tabella <b>«Prodotto»</b> al passo successivo <b>«Valutazione del prodotto»</b>: <b>non serve creare due MUP separati</b>.
            </div>
            <Field label="Compagnia *">
              <Select value={form.company_id} onValueChange={(v) => setForm((f) => ({ ...f, company_id: v, product_id: "" }))}>
                <SelectTrigger data-testid="mup-company-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>Compagnie dirette</SelectLabel>
                    {companies.filter((c) => c.active !== false && c.relation_type !== "collaborazione").map((c) => <SelectItem key={c.company_id} value={c.company_id}>{c.name}</SelectItem>)}
                  </SelectGroup>
                  {companies.some((c) => c.active !== false && c.relation_type === "collaborazione") && (
                    <SelectGroup>
                      <SelectLabel>Collaborazioni</SelectLabel>
                      {companies.filter((c) => c.active !== false && c.relation_type === "collaborazione").map((c) => <SelectItem key={c.company_id} value={c.company_id}>{c.name}</SelectItem>)}
                    </SelectGroup>
                  )}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Prodotto *">
              <Select value={form.product_id} onValueChange={selectProduct}>
                <SelectTrigger data-testid="mup-product-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>{products.filter((p) => p.active && (!form.company_id || p.company_id === form.company_id)).map((p) => <SelectItem key={p.product_id} value={p.product_id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Ramo">
              <Input value={prod?.branch || ""} disabled className="bg-slate-50" />
            </Field>
            <Field label="N. proposta / polizza"><Input data-testid="mup-proposal" value={form.proposal_number} onChange={(e) => set("proposal_number", e.target.value)} /></Field>
          </div>
        )}

        {step === 3 && (
          <MupValutazioneProdotto
            form={form}
            setForm={setForm}
            areas={areas}
            products={products}
            companies={companies}
            reloadCatalog={loadCatalog}
          />
        )}

        {step === 4 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Data compilazione *"><Input data-testid="mup-compilation-date" type="date" value={form.compilation_date} onChange={(e) => set("compilation_date", e.target.value)} /></Field>
            <Field label="Canale di distribuzione *"><Input data-testid="mup-channel" value={form.distribution_channel} onChange={(e) => set("distribution_channel", e.target.value)} placeholder="Es. sede, a distanza…" /></Field>
            <Field label="Preferenza consegna documentazione *">
              <Select value={form.client_data.delivery_pref} onValueChange={(v) => setCD("delivery_pref", v)}>
                <SelectTrigger data-testid="mup-delivery"><SelectValue /></SelectTrigger>
                <SelectContent>{DELIVERY.map((d) => <SelectItem key={d.v} value={d.v}>{d.l}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Modalità firma">
              <Select value={form.otp_signature ? "otp" : "cartacea"} onValueChange={(v) => set("otp_signature", v === "otp")}>
                <SelectTrigger data-testid="mup-signmode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="otp">Firma elettronica con OTP</SelectItem>
                  <SelectItem value="cartacea">Firma cartacea</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <div className="md:col-span-2"><Field label="Note intermediario (compaiono nel fascicolo)"><Textarea data-testid="mup-note-intermediary" value={form.note_intermediary} onChange={(e) => set("note_intermediary", e.target.value)} /></Field></div>
            <div className="md:col-span-2"><Field label="Note cliente (compaiono nel fascicolo)"><Textarea data-testid="mup-note-client" value={form.note_client} onChange={(e) => set("note_client", e.target.value)} /></Field></div>
            <div className="md:col-span-2"><Field label="Note interne (NON compaiono nel fascicolo inviato)"><Textarea data-testid="mup-note-internal" value={form.note_internal} onChange={(e) => set("note_internal", e.target.value)} /></Field></div>
            <div className="md:col-span-2">
              <label className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="mup-banca-etica"
                  className="mt-1 h-4 w-4 accent-brand-blue"
                  checked={!!form.banca_etica_segnalazione}
                  onChange={(e) => set("banca_etica_segnalazione", e.target.checked)}
                />
                <span className="text-sm text-slate-700">
                  <span className="font-medium">Cliente segnalato da Banca Popolare Etica</span> (accordo di collaborazione).
                  Se attivo, nel MUP verrà inserita la dichiarazione prevista sulla segnalazione del contraente.
                </span>
              </label>
              {form.banca_etica_segnalazione && (
                <div className="mt-2">
                  <Field label="Rating di Sostenibilità Assimoco – Eticapro">
                    <Input
                      data-testid="mup-rating-eticapro"
                      value={form.rating_sostenibilita_eticapro}
                      onChange={(e) => set("rating_sostenibilita_eticapro", e.target.value)}
                      placeholder="Es. classe/livello del Rating di Sostenibilità Assimoco – Eticapro"
                    />
                  </Field>
                </div>
              )}
            </div>
            <div className="md:col-span-2 space-y-2">
              <Label className="text-slate-600 text-sm">Consensi privacy del contraente</Label>
              <p className="text-xs text-slate-400">Le scelte del cliente vengono salvate e stampate nel fascicolo (sezione Consensi).</p>
              <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
                {CONSENTS.map((c) => (
                  <div key={c.key} data-testid={`mup-consent-${c.key}`} className="flex items-start justify-between gap-3 p-3">
                    <span className="text-sm text-slate-700 flex-1">{c.label}</span>
                    <Select value={(form.consents || {})[c.key] || "non_autorizzo"} onValueChange={(v) => set("consents", { ...form.consents, [c.key]: v })}>
                      <SelectTrigger data-testid={`mup-consent-select-${c.key}`} className="h-8 w-40 shrink-0"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="autorizzo">Autorizzo</SelectItem>
                        <SelectItem value="non_autorizzo">Non autorizzo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>

            <div className="md:col-span-2 space-y-2">
              <Label className="text-slate-600 text-sm">Ricevuta del contraente, relativa all'informativa e alla consegna della documentazione</Label>
              <p className="text-xs text-slate-400">In ottemperanza all'art. 56 comma 8 del Regolamento IVASS n° 40/2018 il sottoscritto contraente dichiara:</p>
              <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
                {RICEVUTA_DECLS.map((d) => (
                  <label key={d.key} data-testid={`mup-ricevuta-${d.key}`} className="flex items-start gap-3 p-3 cursor-pointer">
                    <input type="checkbox" className="mt-1 h-4 w-4 accent-brand-blue shrink-0"
                      checked={!!(form.ricevuta_contraente || {})[d.key]}
                      onChange={(e) => set("ricevuta_contraente", { ...(form.ricevuta_contraente || {}), [d.key]: e.target.checked })} />
                    <span className="text-sm text-slate-700">{d.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 cursor-pointer" data-testid="mup-salva-assieasy">
                <input type="checkbox" className="h-4 w-4 accent-brand-blue"
                  checked={!!form.salva_assieasy}
                  onChange={(e) => set("salva_assieasy", e.target.checked)} />
                <span className="text-sm text-slate-700"><span className="font-medium">Salva il modulo compilato su AssiEasy</span> <span className="text-slate-400">(segnaposto — integrazione in arrivo)</span></span>
              </label>
            </div>
          </div>
        )}

        {step === 5 && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <SummaryRow k="Cliente" v={form.client_data.name} />
              <SummaryRow k="CF / P.IVA" v={form.client_data.fiscal_code || form.client_data.vat} />
              <SummaryRow k="Intermediario" v={inter?.name} />
              <SummaryRow k="Area di tutela" v={form.area_titolo} />
              <SummaryRow k="Compagnia" v={comp?.name} />
              <SummaryRow k="Prodotto" v={prod?.name} />
              <SummaryRow k="N. proposta/polizza" v={form.proposal_number} />
              <SummaryRow k="Data compilazione" v={form.compilation_date} />
              <SummaryRow k="Canale distribuzione" v={form.distribution_channel} />
              <SummaryRow k="Consegna" v={form.client_data.delivery_pref} />
              <SummaryRow k="Firma" v={form.otp_signature ? "Elettronica con OTP" : "Cartacea"} />
              {form.banca_etica_segnalazione && <SummaryRow k="Segnalazione" v="Banca Popolare Etica (accordo di collaborazione)" />}
              {form.banca_etica_segnalazione && form.rating_sostenibilita_eticapro && <SummaryRow k="Rating di Sostenibilità Assimoco – Eticapro" v={form.rating_sostenibilita_eticapro} />}
            </div>
            {incoherences.length > 0 && !form.incoherence_motivation && (
              <p className="text-sm text-amber-600 flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Ci sono incoerenze senza motivazione: puoi comunque salvare la bozza, ma inseriscila prima dell'invio.</p>
            )}
            <div className="flex items-center gap-2 text-sm text-emerald-600"><CheckCircle2 className="h-4 w-4" /> Dopo il salvataggio potrai generare il PDF, inviarlo e raccogliere la firma dalla scheda pratica.</div>
          </div>
        )}
      </div>

      <div className="flex justify-between mt-6">
        <Button variant="outline" disabled={step === 0} onClick={() => setStep((s) => s - 1)}>
          <ChevronLeft className="h-4 w-4 mr-1" /> Indietro
        </Button>
        {step < STEPS.length - 1 ? (
          <Button data-testid="mup-next-btn" onClick={() => setStep((s) => s + 1)} className="brand-gradient text-white border-0">
            Avanti <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        ) : (
          <Button data-testid="mup-save-btn" disabled={saving || !form.client_data.name || (!form.product_id && !form.area_id)} onClick={() => save(true)} className="brand-gradient text-white border-0">
            {saving ? "Salvataggio…" : editing ? "Salva modifiche" : "Salva pratica"}
          </Button>
        )}
      </div>
    </MupLayout>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-slate-600 text-sm">{label}</Label>
    {children}
  </div>
);

const SummaryRow = ({ k, v }) => (
  <div className="flex justify-between border-b border-slate-100 py-1.5">
    <span className="text-slate-500">{k}</span>
    <span className="text-slate-900 font-medium text-right">{v || "—"}</span>
  </div>
);
